import { Layout, Modal as ModalModule, UI, Utils } from 'common';
import React, { useContext, useEffect, useState } from 'react';
import AccountCommunication from '../utils/AccountCommunication';

const { WrappedInputTextWithLabel, WrappedCheckbox } = UI;
const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { LayoutContext } = Layout;

const AutoFundModal = ({
	data: { callback, AutoFundThresholdLimit, selectedLocations },
	modalId,
}) => {
	const { isMobile } = useContext(LayoutContext);

	const { closeModal, showError } = useContext(ModalContext);

	// disables Update button
	const [isUpdating, setIsUpdating] = useState(false);

	// locations
	const [locations, setLocations] = useState([...selectedLocations]);

	const [enableAutofunding, setEnableAutofunding] = useState(
		locations.every(location => location.AutoPurchase?.Enabled)
	);
	const [purchaseAmount, setPurchaseAmount] = useState('');
	const [thresholdAmount, setThresholdAmount] = useState('');
	const [formComplete, setFormComplete] = useState(true);

	// validations
	const [validations, setValidations] = useState({
		// 0 = untouched, -1 = failed, 1 = valid
		purchaseAmount: 0,
		thresholdAmount: 0,
	});

	const [errorMessages, setErrorMessages] = useState({
		purchaseAmount: '',
		thresholdAmount: '',
	});

	useEffect(() => {
		// enable update button
		if (enableAutofunding) {
			setFormComplete(
				parseFloat(purchaseAmount) >= 100 &&
					parseFloat(thresholdAmount) <= AutoFundThresholdLimit &&
					parseFloat(thresholdAmount) > 1
			);
		}
	}, [purchaseAmount, thresholdAmount]);

	useEffect(() => {
		if (!enableAutofunding) {
			setPurchaseAmount('');
			setThresholdAmount('');
			triggerValidation(false, true);
		} else {
			triggerValidation('enableAutoFunding');
		}
	}, [enableAutofunding]);

	/**
	 * Make API call to update the locations
	 */
	const updateLocations = async () => {
		setIsUpdating(true);

		const response = await AccountCommunication.updateLocations(locations);

		setIsUpdating(false);

		if (!AccountCommunication.isSuccessful(response)) {
			showError({
				response,
			});
		} else {
			closeModal(modalId);
		}

		callback(response);
	};

	const updateLocationsProperty = (propertyName, value) => {
		const updatedLocations = [...locations];

		updatedLocations.forEach((loc, index) => {
			updatedLocations[index][propertyName] = value;
		});

		setLocations(updatedLocations);
	};

	const onUpdateLocationsButtonClick = () => {
		// Update auto-purchase on all locations

		let updatedLocationData = {
			Enabled: enableAutofunding,
		};

		if (enableAutofunding) {
			updatedLocationData = {
				...updatedLocationData,
				PurchaseAmount: purchaseAmount,
				ThresholdAmount: thresholdAmount,
			};
		}

		updateLocationsProperty('AutoPurchase', updatedLocationData);

		// update locations
		updateLocations();
	};

	const triggerValidation = (onBlurField, reset) => {
		const newValidations = { ...validations };
		const newErrorMessages = { ...errorMessages };
		const checkField = {
			purchaseAmount,
			thresholdAmount,
			enableAutofunding,
		};

		Object.keys(validations).forEach(name => {
			const fieldValue = checkField[name];
			if (name === onBlurField) {
				newValidations[name] = fieldValue !== '' ? 1 : -1;
				newErrorMessages[name] = fieldValue !== '' ? '' : '';

				if (name === 'purchaseAmount') {
					if (parseFloat(purchaseAmount) < 100 || purchaseAmount === '') {
						newValidations.purchaseAmount = -1;
						newErrorMessages.purchaseAmount =
							'The minimum automatic purchase amount is $100.';
					}
				} else if (name === 'thresholdAmount') {
					if (
						parseFloat(thresholdAmount) > AutoFundThresholdLimit ||
						thresholdAmount === ''
					) {
						newValidations.thresholdAmount = -1;
						newErrorMessages.thresholdAmount = `This threshold cannot exceed $${AutoFundThresholdLimit} during a bulk update.<br /> To set a higher threshold, edit a location individually.`;
					}
				}
			} else if (reset) {
				newValidations[name] = 0;
			}
		});

		if (reset) {
			setFormComplete(true);
		} else if (enableAutofunding) {
			setFormComplete(
				newValidations.purchaseAmount === 1 &&
					newValidations.thresholdAmount === 1
			);
		}

		setValidations(newValidations);
		setErrorMessages(newErrorMessages);
	};

	const onFieldBlur = ({ target: { name } }) => {
		triggerValidation(name);
	};

	const onFieldChange = ({ target: { name, value, checked } }) => {
		if (name === 'purchaseAmount') {
			setPurchaseAmount(value);
		} else if (name === 'thresholdAmount') {
			setThresholdAmount(value);
		} else if (name === 'enableAutofunding') {
			setEnableAutofunding(checked);
		}
	};

	return (
		<Modal
			modalId={modalId}
			className="account-locations autofund-modal"
			header={`Bulk Update ${locations.length} Locations`}
			primaryButton={{
				type: ModalButtonActionType.Ok,
				onClick: onUpdateLocationsButtonClick,
				text: 'Update Locations',
				disabled: isUpdating || !formComplete,
			}}
			secondaryButton={{
				type: ModalButtonActionType.Cancel,
				text: 'Close',
			}}
			size="small"
		>
			<div className="body">
				<div className="p-grid">
					<div className="p-col-12">
						<WrappedCheckbox
							key="enable-autofund-checkbox"
							dataqa="auto-fund"
							name="enableAutofunding"
							onChange={onFieldChange}
							checked={enableAutofunding}
							inputId="enable-autofund-checkbox"
						>
							Enable Auto-Funding
						</WrappedCheckbox>
					</div>
					<div className="p-col-7">
						<WrappedInputTextWithLabel
							className={`${isMobile ? 'is-mobile' : ''}`}
							label="Automatically purchase $:"
							isInputInvalid={validations.purchaseAmount === -1}
							errorMessage={errorMessages.purchaseAmount}
							uiElementConfig={{
								name: 'purchaseAmount',
								value: purchaseAmount,
								dataqa: 'purchaseAmount',
								disabled: !enableAutofunding,
								onBlur: onFieldBlur,
								onChange: onFieldChange,
							}}
						/>
					</div>
					<div className="p-col-7">
						<WrappedInputTextWithLabel
							className={`${isMobile ? 'is-mobile' : ''}`}
							label="When balance is below $:"
							isInputInvalid={validations.thresholdAmount === -1}
							errorMessage={errorMessages.thresholdAmount}
							uiElementConfig={{
								name: 'thresholdAmount',
								value: thresholdAmount,
								min: 0,
								max: AutoFundThresholdLimit,
								dataqa: 'thresholdAmount',
								disabled: !enableAutofunding,
								onBlur: onFieldBlur,
								onChange: onFieldChange,
							}}
						/>{' '}
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default AutoFundModal;
